import React, { useEffect, useRef } from "react";
import { useLang } from "./LanguageProvider";
import { ArrowRight, Download, ChevronDown } from "lucide-react";
import useCountUp from "@/hooks/useCountUp";
import { CATALOGUE_URL } from "@/lib/siteConfig";

// Hero composition — bright editorial image with factory + family + product on right, white space on left for type
const HERO_BG = "/hero/wellbeing.png";

function StatNumber({ value, suffix = "", testid }) {
  const isNumeric = /^\d+$/.test(String(value).replace(/,/g, ""));
  const numeric = Number(String(value).replace(/,/g, ""));
  const [ref, display] = useCountUp(isNumeric ? numeric : 0, { duration: 1800, suffix });
  return (
    <span
      ref={isNumeric ? ref : null}
      dir="ltr"
      data-testid={testid}
      className="display-num text-brand-navy text-4xl md:text-5xl lg:text-[64px]"
    >
      {isNumeric ? display.replace(/\.0$/, "") : value}
    </span>
  );
}

export default function Hero() {
  const { t, isRtl } = useLang();
  const bgRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    if (window.matchMedia("(pointer: coarse)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const onMove = (e) => {
      if (!bgRef.current) return;
      const x = (e.clientX / window.innerWidth - 0.5) * 10;
      const y = (e.clientY / window.innerHeight - 0.5) * 8;
      const flip = isRtl ? " scaleX(-1)" : "";
      bgRef.current.style.transform = `translate3d(${-x}px, ${-y}px, 0) scale(1.04)${flip}`;
    };

    let attached = false;
    const attach = () => {
      if (attached) return;
      window.addEventListener("mousemove", onMove, { passive: true });
      attached = true;
    };
    const detach = () => {
      if (!attached) return;
      window.removeEventListener("mousemove", onMove);
      attached = false;
    };

    const target = sectionRef.current;
    if (!target) {
      attach();
      return detach;
    }
    const io = new IntersectionObserver(
      ([entry]) => (entry.isIntersecting ? attach() : detach()),
      { threshold: 0 }
    );
    io.observe(target);
    return () => {
      io.disconnect();
      detach();
    };
  }, [isRtl]);

  const goContact = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="hero"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-brand-grey"
    >
      {/* Background image — full bleed, anchored right so the product/family side stays in frame */}
      <div className="absolute inset-0">
        <div
          ref={bgRef}
          className="absolute inset-0 bg-cover bg-right md:bg-[center_right] transition-transform duration-700 ease-out will-change-transform"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            transform: `scale(1.04)${isRtl ? " scaleX(-1)" : ""}`,
          }}
        />
        {/* Editorial fade — solid white on left for type readability, transparent on right to show image */}
        <div
          className={`absolute inset-0 ${
            isRtl
              ? "bg-[linear-gradient(270deg,rgba(244,248,250,0.98)_0%,rgba(244,248,250,0.96)_32%,rgba(244,248,250,0.65)_55%,rgba(244,248,250,0)_82%)]"
              : "bg-[linear-gradient(90deg,rgba(244,248,250,0.98)_0%,rgba(244,248,250,0.96)_32%,rgba(244,248,250,0.65)_55%,rgba(244,248,250,0)_82%)]"
          } md:block hidden`}
        />
        {/* Mobile: stronger overlay top-to-bottom */}
        <div className="absolute inset-0 md:hidden bg-[linear-gradient(180deg,rgba(255,255,255,0.96)_0%,rgba(255,255,255,0.85)_45%,rgba(255,255,255,0.35)_75%,rgba(255,255,255,0)_100%)]" />
        {/* Bottom fade into the dark next section */}
        <div className="absolute bottom-0 inset-x-0 h-40 bg-[linear-gradient(180deg,rgba(244,248,250,0)_0%,#0A1628_100%)]" />
      </div>

      <div className="relative z-10 container mx-auto px-6 md:px-10 pt-44 md:pt-52 pb-32 md:pb-40">
        {/* Editorial masthead */}
        <div className="reveal flex items-baseline justify-between mb-10 md:mb-14 max-w-[680px]">
          <div className="flex items-center gap-3">
            <span className="h-px w-10 bg-brand-teal animate-[shimmer_3s_ease-in-out_infinite]" />
            <span className="text-[11px] md:text-xs uppercase tracking-[0.32em] text-brand-teal font-semibold">
              {t.hero.kicker}
            </span>
          </div>
        </div>

        <div className="max-w-[820px]">
          <h1
            data-testid="hero-title"
            className="reveal reveal-delay-1 font-heading text-brand-navy text-[44px] leading-[0.98] sm:text-6xl md:text-7xl lg:text-[96px] font-medium tracking-tight"
          >
            {t.hero.title_1}
            <br />
            <span className="relative inline-block">
              <span className="text-brand-teal">{t.hero.title_2}</span>
              <span className="absolute -bottom-2 left-0 right-0 h-[2px] bg-gradient-to-r from-brand-teal/0 via-brand-teal to-brand-teal/0 origin-left scale-x-0 animate-[scaleX_1.4s_1.2s_forwards]" />
            </span>
          </h1>

          <p
            className={`reveal reveal-delay-2 mt-8 md:mt-10 text-base md:text-lg lg:text-xl text-brand-navy/75 max-w-xl leading-relaxed ${isRtl ? "text-right" : "text-left"}`}
          >
            {t.hero.subtitle}
          </p>

          <div className="reveal reveal-delay-3 mt-10 md:mt-12 flex flex-col sm:flex-row gap-4">
            <button
              onClick={goContact}
              data-testid="hero-cta-primary"
              className="group inline-flex items-center justify-center gap-3 bg-brand-teal hover:bg-brand-tealDark text-white px-7 py-4 rounded-full text-sm md:text-base font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-12px_rgba(46,132,160,0.45)]"
            >
              {t.hero.cta_primary}
              <ArrowRight size={18} className={`transition-transform group-hover:translate-x-1 ${isRtl ? "rotate-180 group-hover:-translate-x-1" : ""}`} />
            </button>
            <a
              href={CATALOGUE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-3 border border-brand-navy/20 hover:border-brand-navy/60 bg-white/70 hover:bg-white text-brand-navy px-7 py-4 rounded-full text-sm md:text-base font-medium transition-all duration-300"
            >
              <Download size={18} strokeWidth={1.6} />
              {t.hero.cta_secondary}
            </a>
          </div>
        </div>

        {/* Stats row */}
        <dl className="reveal reveal-delay-4 mt-20 md:mt-32 grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6 md:gap-x-12 border-t border-brand-navy/10 pt-10 md:pt-12 max-w-[1100px]">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} data-testid={`hero-stat-${n}`} className="flex flex-col gap-3">
              <div aria-hidden="true" className="text-[10px] uppercase tracking-[0.28em] text-brand-teal font-semibold">
                · 0{n}
              </div>
              <dt className="sr-only">{t.hero[`stat_${n}_label`]}</dt>
              <dd>
                <StatNumber value={t.hero[`stat_${n}`]} testid={`hero-stat-num-${n}`} />
              </dd>
              <span aria-hidden="true" className="text-[11px] md:text-xs uppercase tracking-[0.18em] text-brand-navy/55 leading-snug max-w-[180px]">
                {t.hero[`stat_${n}_label`]}
              </span>
            </div>
          ))}
        </dl>
      </div>

      {/* Scroll cue */}
      <div className="hidden md:flex absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex-col items-center gap-2 text-brand-navy/40">
        <span className="text-[10px] uppercase tracking-[0.32em]">scroll</span>
        <ChevronDown size={16} className="animate-bounce" />
      </div>

      <style>{`
        @keyframes scaleX {
          to { transform: scaleX(1); }
        }
      `}</style>
    </section>
  );
}
